// Timed blind doubles. Host setting `settings.blindsGoUpMinutes`
// (integer 0–180, default 0 / Off). Clock uses `settings.blindsUpStartedAt`
// (Firebase server timestamp). Doubles on the NEXT deal after expiry, never
// mid-hand. Never edit pokerLogic.js.

export const BLINDS_GO_UP_MAX = 180;

export function parseBlindsGoUpInput(raw) {
  if (raw == null || raw === '') return { ok: true, minutes: 0 };
  const s = String(raw).trim();
  if (s === '') return { ok: true, minutes: 0 };
  if (!/^\d+$/.test(s)) return { ok: false, minutes: 0 };
  const n = Number(s);
  if (n === 0) return { ok: true, minutes: 0 };
  if (n >= 1 && n <= BLINDS_GO_UP_MAX) return { ok: true, minutes: n };
  return { ok: false, minutes: 0 };
}

export function blindsGoUpMinutes(settings) {
  const parsed = parseBlindsGoUpInput(settings?.blindsGoUpMinutes);
  return parsed.ok ? parsed.minutes : 0;
}

export function minutesInputValue(minutes) {
  const n = parseBlindsGoUpInput(minutes).minutes;
  return n > 0 ? String(n) : '';
}

export function blindsUpStartedAt(settings) {
  const n = Number(settings?.blindsUpStartedAt);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function blindsUpEndsAt(settings, minutes = blindsGoUpMinutes(settings)) {
  if (!minutes) return null;
  const started = blindsUpStartedAt(settings);
  if (!started) return null;
  return started + minutes * 60 * 1000;
}

export function remainingMs(endsAt, now) {
  if (typeof endsAt !== 'number') return null;
  return endsAt - now;
}

export function isBlindsUpExpired(settings, now) {
  const minutes = blindsGoUpMinutes(settings);
  const ends = blindsUpEndsAt(settings, minutes);
  if (!minutes || ends == null) return false;
  return now >= ends;
}

export function formatCountdown(ms) {
  const total = Math.max(0, Math.floor(Number(ms) / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function doubledBlinds(smallBlind, bigBlind) {
  return {
    smallBlind: Math.round(Number(smallBlind) || 0) * 2,
    bigBlind: Math.round(Number(bigBlind) || 0) * 2,
  };
}

/** NEXT $2/$4 line — doubled blinds, room currency. */
export function nextBlindsLabel(smallBlind, bigBlind, formatCurrency) {
  const next = doubledBlinds(smallBlind, bigBlind);
  return `NEXT ${formatCurrency(next.smallBlind)}/${formatCurrency(next.bigBlind)}`;
}

/**
 * Zero-state amount only: drop trailing .00 on whole dollars, keep cents
 * when non-zero. Does not change formatCurrency or the running NEXT line.
 */
export function formatLevelAmount(cents, formatCurrency) {
  const n = Math.round(Number(cents) || 0);
  const formatted = formatCurrency(n);
  if (n % 100 !== 0) return formatted;
  return String(formatted).replace(/\.00(?!\d)/, '');
}

/** Zero-state second line — doubled blinds, no NEXT prefix. */
export function nextLevelLabel(smallBlind, bigBlind, formatCurrency) {
  const next = doubledBlinds(smallBlind, bigBlind);
  return `${formatLevelAmount(next.smallBlind, formatCurrency)}/${formatLevelAmount(next.bigBlind, formatCurrency)}`;
}

function intervalMinutes(minutes) {
  const parsed = parseBlindsGoUpInput(minutes);
  return parsed.ok ? parsed.minutes : 0;
}

/**
 * Deal-time plan. If the clock has expired, double THIS deal's blinds and
 * restart. If the interval is on but the clock has not started (first hand),
 * start it. Off clears a leftover startedAt.
 *
 * `startedAtWrite` is the value to persist for a new clock (Firebase
 * serverTimestamp() at the call site).
 */
export function planBlindsGoUpOnDeal({
  minutes,
  startedAt,
  smallBlind,
  bigBlind,
  now,
  startedAtWrite,
}) {
  const interval = intervalMinutes(minutes);
  const sb = Math.round(Number(smallBlind) || 0);
  const bb = Math.round(Number(bigBlind) || 0);
  const started = Number.isFinite(Number(startedAt)) && Number(startedAt) > 0
    ? Number(startedAt)
    : null;

  if (!interval) {
    return {
      smallBlind: sb,
      bigBlind: bb,
      doubled: false,
      updates: started != null ? { 'settings/blindsUpStartedAt': null } : {},
    };
  }

  const endsAt = started != null ? started + interval * 60 * 1000 : null;
  if (endsAt != null && now >= endsAt) {
    const next = doubledBlinds(sb, bb);
    return {
      smallBlind: next.smallBlind,
      bigBlind: next.bigBlind,
      doubled: true,
      updates: {
        'settings/smallBlind': next.smallBlind,
        'settings/bigBlind': next.bigBlind,
        'settings/blindsUpStartedAt': startedAtWrite,
      },
    };
  }

  if (started == null) {
    return {
      smallBlind: sb,
      bigBlind: bb,
      doubled: false,
      updates: { 'settings/blindsUpStartedAt': startedAtWrite },
    };
  }

  return { smallBlind: sb, bigBlind: bb, doubled: false, updates: {} };
}

/**
 * Host types minutes (empty/0 = Off, 1–180). Timer starts when PLAYING
 * (mid-game on, or interval change). Lobby only stores the interval — first
 * deal starts the clock. Same interval while PLAYING does not restart.
 */
export function planBlindsGoUpSetting({
  minutes,
  playing,
  startedAtWrite,
  previousMinutes = null,
}) {
  const interval = intervalMinutes(minutes);
  const prev = intervalMinutes(previousMinutes);
  const updates = { 'settings/blindsGoUpMinutes': interval };
  if (interval === 0) {
    updates['settings/blindsUpStartedAt'] = null;
  } else if (playing && interval !== prev) {
    updates['settings/blindsUpStartedAt'] = startedAtWrite;
  }
  return { minutes: interval, updates };
}
